import { useState } from 'react';
import { Modal } from '../Modal';
import { ModalFooter } from '../ModalFooter';
import { FormGroup } from '../FormGroup';
import { ToggleSwitch } from '../ToggleSwitch';
import { useCreateReport } from '../../hooks/useReports';
import { notifyError } from '../../services/notify';
import { getPreference, setPreference } from '../../services/angular-bridge';
import { EMPTY_EXPRESSION } from '../../services/resourceDatasource';
import type { BulkContext } from '../AddTagControl';

const DEFAULT_DATETIME = "yyyy-MM-dd'T'HH:mm:ss.SSSZ";

// Angular Preferences.TAG_REPORT_* keys and the PreferenceService first-run defaults.
const PREF = { project: 'trp', system: 'trs', domain: 'trd', batch: 'trb' } as const;
const pref = (key: string, fallback: boolean) => getPreference<boolean>(key) ?? fallback;

/**
 * Tag Report — React port of dialog_tag_report.html / AddTagReportModalCtrl. Reports on
 * the current view (collections + tag filter). Requires at least one tag category; the
 * payload always uses format 'zip' with an empty fields list. The category switches start
 * from the user's last-submitted choices.
 */
export function TagReportModal({ bulk, onClose }: { bulk: BulkContext; onClose: () => void }) {
  const createReport = useCreateReport(bulk.projectId);

  const [name, setName] = useState(`Tag Report ${new Date().toLocaleString()}`);
  const [includeProjectTags, setProject] = useState(() => pref(PREF.project, true));
  const [includeSystemTags, setSystem] = useState(() => pref(PREF.system, true));
  const [includeDomainTags, setDomain] = useState(() => pref(PREF.domain, false));
  const [includeBatchTags, setBatch] = useState(() => pref(PREF.batch, false));

  const anyCategory = includeProjectTags || includeSystemTags || includeDomainTags || includeBatchTags;
  const valid = name.trim().length > 0 && name.length <= 255;

  const submit = () => {
    if (!valid) return;
    if (!anyCategory) {
      // Mirrors the Angular controller's client-side guard (not a server failure).
      notifyError({ title: 'Error', description: 'You must choose at least one tag category' });
      return;
    }
    setPreference(PREF.project, includeProjectTags);
    setPreference(PREF.system, includeSystemTags);
    setPreference(PREF.domain, includeDomainTags);
    setPreference(PREF.batch, includeBatchTags);
    createReport.mutate(
      {
        projectId: bulk.projectId,
        type: bulk.search ? 'tag_search_report' : 'tag_report',
        format: 'zip',
        name,
        multiValueCharCode: 59,
        dateTimeFormat: DEFAULT_DATETIME,
        hasHeaderRow: true,
        fields: [],
        collectionIds: bulk.collectionIds,
        filter: bulk.filter,
        // Browse reports become the server's searchExpression; it must be an object (Angular
        // always sent one, empty when no column filters).
        columnFilters: bulk.columnFilters ?? EMPTY_EXPRESSION,
        includeProjectTags,
        includeSystemTags,
        includeDomainTags,
        includeBatchTags,
        // add_tag_report_modal.js:92-96 — unlike the resource report, this one keeps the
        // two apart: basic search in `query`, advanced expression in `searchExpression`.
        ...(bulk.search
          ? {
              query: bulk.search.advanced ? '' : bulk.search.query,
              searchExpression: bulk.search.advanced ? bulk.search.expression : null,
            }
          : {}),
      },
      { onSuccess: onClose }
    );
  };

  return (
    <Modal
      title="Generate Tag Report"
      testId="reports-tag-modal"
      icon="fas fa-file-alt"
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      formClassName="form-horizontal"
      footer={
        <ModalFooter onClose={onClose} nextLabel="Save" nextDisabled={!valid}
          saving={createReport.isPending} savingLabel="Saving…" />
      }
    >
      <FormGroup label="Project" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
        <p className="form-control-static"><i className="fas fa-gavel" /> {bulk.projectName ?? ''}</p>
      </FormGroup>

      <FormGroup label="Report name" htmlFor="tr-name" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
        <input id="tr-name" data-testid="reports-tag-name-input" type="text" className="form-control" value={name} maxLength={255}
          placeholder="Report Name" disabled={createReport.isPending} onChange={(e) => setName(e.target.value)} />
      </FormGroup>

      <FormGroup label="Project Tags" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
        <ToggleSwitch testId="reports-tag-project-tags-toggle" checked={includeProjectTags} onChange={setProject} disabled={createReport.isPending} />
      </FormGroup>
      <FormGroup label="System Tags" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
        <ToggleSwitch testId="reports-tag-system-tags-toggle" checked={includeSystemTags} onChange={setSystem} disabled={createReport.isPending} />
      </FormGroup>
      <FormGroup label="Domain Tags" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
        <ToggleSwitch testId="reports-tag-domain-tags-toggle" checked={includeDomainTags} onChange={setDomain} disabled={createReport.isPending} />
      </FormGroup>
      <FormGroup label="Batch Tags" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
        <ToggleSwitch testId="reports-tag-batch-tags-toggle" checked={includeBatchTags} onChange={setBatch} disabled={createReport.isPending} />
      </FormGroup>
    </Modal>
  );
}
